import { RobotCloudAPI, } from '@moderepo/mode-apis';
import { searchParamsToString, } from '@moderepo/mode-data-state-base';
/**
 * The types of action that can be dispatched to update this sub state.
 */
export var FileRequestStateActionType;
(function (FileRequestStateActionType) {
    FileRequestStateActionType["SET_FILE_REQUESTS"] = "set file requests";
    FileRequestStateActionType["SET_FILE_REQUEST"] = "set file request";
    FileRequestStateActionType["CLEAR_FILE_REQUESTS"] = "clear file requests";
})(FileRequestStateActionType || (FileRequestStateActionType = {}));
export const setFileRequests = (homeId, fileRequests, searchParams) => {
    return {
        type: FileRequestStateActionType.SET_FILE_REQUESTS,
        homeId,
        fileRequests,
        searchParams,
    };
};
export const setFileRequest = (homeId, fileRequest) => {
    return {
        type: FileRequestStateActionType.SET_FILE_REQUEST,
        homeId,
        fileRequest,
    };
};
export const clearFileRequests = (homeId) => {
    return {
        type: FileRequestStateActionType.CLEAR_FILE_REQUESTS,
        homeId,
    };
};
/**
 * Get all fileRequests for a project with the given filters
 * @param homeId - The id of the home
 */
export const fetchAllFileRequests = (homeId, filters) => {
    return async (dataDispatch) => {
        var _a;
        const modifiedFilters = {
            ...filters,
            skip: filters.pageNumber * filters.pageSize,
            limit: (_a = filters.pageSize) !== null && _a !== void 0 ? _a : 0,
            pageNumber: undefined,
            pageSize: undefined,
        };
        const fileRequests = await RobotCloudAPI.getInstance().getAllFileRequests(homeId, modifiedFilters);
        await dataDispatch(setFileRequests(homeId, fileRequests, searchParamsToString(filters)));
    };
};
/**
 * Get 1 file request
 * @param homeId - The id of the home
 * @param requestId - The id of the request
 */
export const fetchFileRequestById = (homeId, requestId) => {
    return async (dataDispatch) => {
        const request = await RobotCloudAPI.getInstance().getFileRequestById(homeId, requestId);
        await dataDispatch(setFileRequest(homeId, request));
    };
};
/**
 * Create a request to DOWNLOAD a file from a DEVICE
 * @param homeId
 * @param deviceId
 * @param fileType
 * @param startDate
 * @param endDate
 * @returns
 */
export const createDownloadFileFromDeviceRequest = (homeId, deviceId, fileType, startDate, endDate, useCustomDriver) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().createDownloadFileFromDeviceRequest(homeId, deviceId, fileType, startDate, endDate, useCustomDriver);
        await dataDispatch(clearFileRequests(homeId));
    };
};
/**
 * Create a request to DOWNLOAD a file from a CONTROL SERVER
 * @param homeId
 * @param deviceId
 * @param fileType
 * @param startDate
 * @param endDate
 * @returns
 */
export const createDownloadFileFromControlServerRequest = (homeId, deviceId, fileType, startDate, endDate, useCustomDriver) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().createDownloadFileFromControlServerRequest(homeId, deviceId, fileType, startDate, endDate, useCustomDriver);
        await dataDispatch(clearFileRequests(homeId));
    };
};
/**
 * Create a request to DOWNLOAD a file from a ROBOT
 * @param homeId
 * @param deviceId
 * @param robotId
 * @param fileType
 * @param startDate
 * @param endDate
 * @returns
 */
export const createDownloadFileFromRobotRequest = (homeId, deviceId, robotId, fileType, startDate, endDate, useCustomDriver) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().createDownloadFileFromRobotRequest(homeId, deviceId, robotId, fileType, startDate, endDate, useCustomDriver);
        await dataDispatch(clearFileRequests(homeId));
    };
};
/**
 * Update an UPLOAD file to DEVICE request that is in INITIATED status
 * @param homeId - The id of the home
 * @param requestId - The id of the request
 */
export const updateUploadFileToDeviceRequestMetadata = (homeId, requestId, params) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().updateUploadFileToDeviceRequestMetadata(homeId, requestId, params);
        // refetch request info
        await dataDispatch(fetchFileRequestById(homeId, requestId));
    };
};
/**
 * Update an UPLOAD file to CONTROL SERVER request that is in INITIATED status
 * @param homeId - The id of the home
 * @param requestId - The id of the request
 */
export const updateUploadFileToControlServerRequestMetadata = (homeId, requestId, params) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().updateUploadFileToControlServerRequestMetadata(homeId, requestId, params);
        // refetch request info
        await dataDispatch(fetchFileRequestById(homeId, requestId));
    };
};
/**
 * Update an UPLOAD file to ROBOTS request that is in INITIATED status
 * @param homeId - The id of the home
 * @param requestId - The id of the request
 */
export const updateUploadFileToRobotsRequestMetadata = (homeId, requestId, robotId, params) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().updateUploadFileToRobotsRequestMetadata(homeId, requestId, robotId, params);
        // refetch request info
        await dataDispatch(fetchFileRequestById(homeId, requestId));
    };
};
/**
 * To cancel only UPLOAD file to Control Server/Robot requests which are in INITIATED status. These are the requests which require the frontend
 * to upload the file to S3 first and the user decided to cancel it before uploading the file to S3. But once the file is already uploaded
 * to S3 and the frontend already called 'updateUploadFileToControlServerRequestMetadata' to notify the backend, and backend is uploading the
 * files to the control server or robot, then don't use this API to cancel the request, use the 'cancelPendingFileRequest' function.
 * @param homeId
 * @param requestId
 */
export const cancelInitiatedFileUploadRequest = (homeId, requestId) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().cancelInitiatedFileUploadRequest(homeId, requestId);
        // refetch request info
        await dataDispatch(fetchFileRequestById(homeId, requestId));
    };
};
/**
 * To cancel any request that is in PENDING status. This can be used for Download/Upload requests to Control Server/Robot.
 * @param homeId
 * @param requestId
 */
export const cancelPendingFileRequest = (homeId, requestId) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().cancelPendingFileRequest(homeId, requestId);
        // refetch request info
        await dataDispatch(fetchFileRequestById(homeId, requestId));
    };
};
/**
 * Delete a request
 * @param homeId - The id of the home
 * @param requestId - The id of the request
 */
export const deleteFileRequest = (homeId, requestId) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().deleteFileRequest(homeId, requestId);
        await dataDispatch(clearFileRequests(homeId));
    };
};
